/*
 * Redaction before anything reaches the model (docs/assistant.md, principle
 * 7): the person's messages, the context and every tool result pass through
 * here. Private keys, tokens and passwords are found by pattern, `.env`-like
 * lines by their key, JSON fields by a name that says secret. What the
 * patterns miss stays with the panel's own rule: secrets are never in our
 * API's answers to begin with (root passwords are shown once, keys hashed).
 * Shared: the panel redacts a screen snapshot with it before sending, and
 * the API redacts everything again (`apps/api/src/assistant/redact.ts`).
 */

const HIDDEN = "[redacted]";

/** Whole secrets of a known shape, replaced by a label of what they were. */
const PATTERNS: readonly [RegExp, string][] = [
  [
    /-----BEGIN [A-Z0-9 ]*PRIVATE KEY(?: BLOCK)?-----[\s\S]*?(?:-----END [A-Z0-9 ]*PRIVATE KEY(?: BLOCK)?-----|$)/g,
    "[redacted: private key]",
  ],
  // Our own API keys (docs/api-keys.md).
  [/\bcsk_[0-9a-f]{32}_[A-Za-z0-9_-]{43}\b/g, "[redacted: API key]"],
  [/\bsk-ant-[A-Za-z0-9_-]{16,}/g, "[redacted: API key]"],
  [/\bsk-(?:proj-)?[A-Za-z0-9_-]{20,}/g, "[redacted: API key]"],
  [/\b(?:gh[pousr]_[A-Za-z0-9]{30,}|github_pat_[A-Za-z0-9_]{40,})\b/g, "[redacted: token]"],
  [/\bglpat-[A-Za-z0-9_-]{20,}\b/g, "[redacted: token]"],
  [/\bxox[abposr]-[A-Za-z0-9-]{10,}\b/g, "[redacted: token]"],
  [/\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g, "[redacted: access key]"],
  [/\bAIza[0-9A-Za-z_-]{35}\b/g, "[redacted: API key]"],
  [/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, "[redacted: token]"],
  // Telegram bot tokens (docs/alerts.md).
  [/\b\d{6,12}:[A-Za-z0-9_-]{35}\b/g, "[redacted: token]"],
  // Tailscale auth keys, OAuth client secrets and API keys (docs/packages.md, "Tailscale").
  [/\btskey-[A-Za-z0-9-]{10,}/g, "[redacted: Tailscale key]"],
];

/** `Authorization: Bearer …`, `Basic …`: the scheme stays, the credential goes. */
const AUTH_SCHEME = /\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{12,}/g;
/** `scheme://user:password@host`: the user and password go. */
const URL_CREDENTIALS = /\b([a-z][a-z0-9+.-]*:\/\/)[^\s:/@]+:[^\s@/]+@/gi;
/** A key that names a secret, in `.env`, YAML, INI or shell assignments. */
const SECRET_NAME =
  "[A-Za-z0-9_.-]*(?:PASSWORD|PASSWD|PASSPHRASE|SECRET|TOKEN|API_?KEY|ACCESS_?KEY|PRIVATE_?KEY|CREDENTIALS?|AUTH_?KEY|COOKIE)[A-Za-z0-9_.-]*";
const SECRET_LINE = new RegExp(
  `^(\\s*(?:export\\s+)?["']?${SECRET_NAME}["']?\\s*[=:]\\s*)(?!\\s*$)(?!\\[redacted)(.+)$`,
  "gim",
);
/** The same anywhere in a line (`connecting with password=…`, `"token": "…"`), up to the value's end. */
const SECRET_PAIR = new RegExp(
  `\\b(${SECRET_NAME}["']?\\s*[=:]\\s*)(?!\\[redacted)("[^"\\n]*"|'[^'\\n]*'|[^\\s"',;]+)`,
  "gi",
);
/** A Hetzner API token and the like: 64 letters and digits, mixed case (hex digests stay). */
const LONG_TOKEN = /\b[A-Za-z0-9]{64}\b/g;

/** A JSON field whose name says it holds a secret. */
const SECRET_FIELD =
  /password|passwd|passphrase|secret|token|api_?key|apikey|private_?key|privatekey|credential|authorization|cookie/i;

export function redactText(text: string): string {
  let out = text;
  for (const [pattern, label] of PATTERNS) out = out.replace(pattern, label);
  out = out.replace(AUTH_SCHEME, (_, scheme: string) => `${scheme} ${HIDDEN}`);
  out = out.replace(URL_CREDENTIALS, (_, scheme: string) => `${scheme}${HIDDEN}@`);
  out = out.replace(SECRET_LINE, (_, key: string) => `${key}${HIDDEN}`);
  out = out.replace(SECRET_PAIR, (_, key: string) => `${key}${HIDDEN}`);
  out = out.replace(LONG_TOKEN, (token) =>
    /[A-Z]/.test(token) && /[a-z]/.test(token) && /\d/.test(token) ? HIDDEN : token,
  );
  return out;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** A JSON value with secret fields hidden and every string redacted. */
export function redactValue(value: unknown): unknown {
  if (typeof value === "string") return redactText(value);
  if (Array.isArray(value)) return value.map(redactValue);
  if (!isObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, inner]) => [
      key,
      SECRET_FIELD.test(key) && typeof inner === "string" && inner !== ""
        ? HIDDEN
        : redactValue(inner),
    ]),
  );
}

/**
 * Text that addresses an AI rather than a person reading data ("ignore the
 * previous instructions"). Tool results are data whatever they say; when one
 * holds such text the assistant tells the person it did not follow it.
 */
const INJECTION = [
  /\bignore\s+(?:all\s+|any\s+)?(?:the\s+)?(?:previous|prior|above|earlier)\s+(?:instructions|prompts?|rules)/i,
  /\bdisregard\s+(?:all\s+|the\s+)?(?:previous|prior|above|system)\b/i,
  /\byou\s+are\s+now\s+(?:a|an|in)\b/i,
  /\b(?:new|updated|override)\s+system\s+(?:prompt|instructions?)\b/i,
  /<\/?(?:system|instructions?)>/i,
  /\b(?:as\s+an?\s+)?(?:ai|llm|assistant|claude|chatgpt),?\s+(?:you\s+must|please)\s+(?:run|execute|delete|call|send)\b/i,
  /игнорируй(?:те)?\s+(?:все\s+)?(?:предыдущие|прошлые|прежние)\s+(?:инструкции|указания|правила)/i,
  /забудь(?:те)?\s+(?:все\s+)?(?:предыдущие|прошлые)\s+(?:инструкции|указания)/i,
  /ты\s+теперь\s+(?:—\s*)?(?:не\s+)?(?:ассистент|бот|ии)/i,
];

export function looksLikeInjection(text: string): boolean {
  return INJECTION.some((pattern) => pattern.test(text));
}
